import { useQuery } from '@tanstack/react-query'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Plus, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  deleteStakeholder,
  SLUG_TO_KIND,
  stakeholderKeys,
  stakeholdersQueryOptions,
  type StakeholderSlug,
} from '@/features/stakeholders/api'
import { ContactDetail } from '@/features/stakeholders/contact-detail'
import { ContactPrintSheet } from '@/features/stakeholders/contact-print-sheet'
import {
  contatosSearchSchema,
  toStakeholderListParams,
  type ContatosSearch,
} from '@/features/stakeholders/search'
import { StakeholderFormDrawer } from '@/features/stakeholders/stakeholder-form-drawer'
import type { StakeholderOut } from '@/shared/api/types'
import { cn } from '@/shared/lib/cn'
import { formatDocument } from '@/shared/lib/document'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { PrintButton } from '@/shared/ui/print-button'
import { Skeleton } from '@/shared/ui/skeleton'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { PrintHeader } from '@/widgets/print/print-header'
import { ErrorState } from '@/widgets/data-table/error-state'
import { NoResultsState } from '@/widgets/data-table/no-results-state'
import { RowDeleteButton } from '@/widgets/data-table/row-delete-button'
import { TablePager } from '@/widgets/data-table/table-pager'

/**
 * UMA rota para os 4 tipos de contato.
 *
 * Clientes/Fornecedores/Funcionários/Sócios são a MESMA entidade no backend
 * (`stakeholder` + discriminador `kind`). Quatro arquivos seriam quatro cópias
 * divergindo com o tempo. A sub-nav (as 4 abas) vem do `PageHeader`
 * (`navTabsFor`), não é montada aqui.
 */
export const Route = createFileRoute('/_authed/contatos/$slug')({
  params: {
    parse: (raw) => {
      const slug = raw.slug
      if (!(slug in SLUG_TO_KIND)) throw notFound()
      return { slug: slug as StakeholderSlug }
    },
    stringify: (p) => ({ slug: p.slug }),
  },
  validateSearch: contatosSearchSchema,
  loaderDeps: ({ search }) => ({ search }),
  loader: ({ context, params, deps }) =>
    context.queryClient.ensureQueryData(
      stakeholdersQueryOptions(toStakeholderListParams(deps.search, SLUG_TO_KIND[params.slug])),
    ),
  component: ContatosPage,
})

/** slug → sufixo da chave i18n (namespace nav). Literal, para o `t()` tipado aceitar. */
const NAV_LABEL = {
  clientes: 'customers',
  fornecedores: 'suppliers',
  funcionarios: 'employees',
  socios: 'partners',
} as const satisfies Record<StakeholderSlug, string>

function ContatosPage() {
  // Anotação explícita: `useParams()` chega como `any`, e sem o tipo o `slug`
  // não indexa SLUG_TO_KIND/NAV_LABEL (TS7053). O `params.parse` da rota já
  // garante o estreitamento — slug fora da lista lança notFound() antes daqui.
  const { slug }: { slug: StakeholderSlug } = Route.useParams()
  const kind = SLUG_TO_KIND[slug]
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { t } = useTranslation(['contacts', 'nav', 'common'])
  const [drawerAberto, setDrawerAberto] = useState(false)
  // null = criar; contato = editar. Alimenta a `key` do drawer (remonta o form).
  const [emEdicao, setEmEdicao] = useState<StakeholderOut | null>(null)
  // Contato selecionado no master-detail. Se some da página (troca de aba,
  // filtro, exclusão), o fallback é o primeiro item — sem estado órfão.
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null)

  const abrirCriar = () => {
    setEmEdicao(null)
    setDrawerAberto(true)
  }

  const aplicar = (patch: Partial<ContatosSearch>) => {
    // `to` + `params` EXPLÍCITOS e `prev` ANOTADO: sem eles o destino fica
    // ambíguo, o updater cai em `any` (TS7006) e a navegação pode virar no-op.
    // Fica na MESMA aba — o slug atual é repassado de volta.
    void navigate({
      to: '/contatos/$slug',
      params: { slug },
      search: (prev: ContatosSearch) => ({ ...prev, ...patch }),
      replace: true,
    })
  }
  /** Filtrar/buscar sempre volta para a página 1 (senão abre num offset vazio). */
  const filtrar = (patch: Partial<ContatosSearch>) => {
    aplicar({ ...patch, pagina: 1 })
  }

  const { data, isLoading, error, refetch } = useQuery(
    stakeholdersQueryOptions(toStakeholderListParams(search, kind)),
  )

  const itens = data?.items ?? []
  const selecionado = itens.find((s) => s.id === selecionadoId) ?? itens[0] ?? null
  const buscando = search.q !== undefined

  const botaoNovo = (
    <Button variant="primary" onClick={abrirCriar}>
      <Plus aria-hidden />
      {t(`contacts:new.${kind}`)}
    </Button>
  )

  // Título da aba (Clientes/Fornecedores/…), reusado no header e na impressão.
  const titulo = t(`nav:contacts.${NAV_LABEL[slug]}`)

  return (
    <>
      <PageHeader
        title={titulo}
        actions={
          <>
            <PrintButton />
            {botaoNovo}
          </>
        }
      />

      <div className="p-6">
        <PrintHeader title={titulo} />
        {/* Folha de impressão do contato SELECIONADO (só na impressão). */}
        {selecionado !== null && <ContactPrintSheet stakeholder={selecionado} />}
        {/* Master-detail interativo — escondido na impressão. */}
        <div data-print-hide className="grid gap-4 lg:grid-cols-[minmax(260px,340px)_1fr]">
          {/* COLUNA ESQUERDA — busca + lista selecionável. */}
          <div className="flex flex-col gap-3">
            <Input
              type="search"
              value={search.q ?? ''}
              onChange={(e) => {
                filtrar({ q: e.target.value === '' ? undefined : e.target.value })
              }}
              placeholder={t('contacts:filters.searchPlaceholder')}
              aria-label={t('contacts:filters.searchPlaceholder')}
            />

            <div className="border-line bg-surface overflow-hidden rounded-lg border">
              {isLoading ? (
                <ul className="divide-line divide-y" aria-hidden>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <li key={i} className="px-4 py-3">
                      <Skeleton className="h-4 w-2/3" />
                    </li>
                  ))}
                </ul>
              ) : error ? (
                <div className="p-4">
                  <ErrorState error={error} onRetry={() => void refetch()} />
                </div>
              ) : itens.length === 0 ? (
                buscando ? (
                  <div className="p-4">
                    <NoResultsState
                      onLimpar={() => {
                        filtrar({ q: undefined })
                      }}
                    />
                  </div>
                ) : (
                  <EmptyState
                    icon={Users}
                    title={t(`contacts:empty.${kind}.title`)}
                    description={t(`contacts:empty.${kind}.description`)}
                    action={botaoNovo}
                  />
                )
              ) : (
                <ul className="divide-line divide-y" aria-label={t('contacts:ariaTable')}>
                  {itens.map((s) => {
                    const ativo = s.id === selecionado?.id
                    return (
                      <li key={s.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setSelecionadoId(s.id)
                          }}
                          aria-current={ativo ? 'true' : undefined}
                          className={cn(
                            'w-full px-4 py-3 text-left transition-colors',
                            ativo ? 'bg-action-bg' : 'hover:bg-surface-2',
                          )}
                        >
                          <p
                            className={cn(
                              'truncate text-sm',
                              ativo ? 'text-action font-medium' : 'text-ink',
                            )}
                          >
                            {s.name}
                          </p>
                          {s.document !== null && s.document !== '' && (
                            <p className="text-ink-subtle truncate text-xs">
                              {formatDocument(s.document)}
                            </p>
                          )}
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {itens.length > 0 && (
              <TablePager
                total={data?.total ?? 0}
                pagina={search.pagina}
                linhas={search.linhas}
                onPageChange={(pagina) => {
                  aplicar({ pagina })
                }}
                onLinhasChange={(linhas) => {
                  aplicar({ linhas, pagina: 1 })
                }}
              />
            )}
          </div>

          {/* COLUNA DIREITA — detalhe do contato selecionado. */}
          {selecionado !== null ? (
            <ContactDetail
              // Remonta ao trocar de contato (queries de agregado são por id).
              key={selecionado.id}
              stakeholder={selecionado}
              onEdit={() => {
                setEmEdicao(selecionado)
                setDrawerAberto(true)
              }}
              headerActions={
                <RowDeleteButton
                  ariaLabel={t('contacts:editAria', { name: selecionado.name })}
                  confirmTitle={t('contacts:deleteConfirm.title', { name: selecionado.name })}
                  confirmDescription={t('contacts:deleteConfirm.description')}
                  successMessage={t('contacts:deleted', { name: selecionado.name })}
                  onDelete={() => deleteStakeholder(selecionado.id)}
                  invalidateKey={stakeholderKeys.lists()}
                  conflictMessages={{
                    stakeholder_has_schedules: t('contacts:errorHasSchedules'),
                  }}
                />
              }
            />
          ) : (
            <div className="border-line text-ink-subtle hidden items-center justify-center rounded-lg border border-dashed p-10 text-sm lg:flex">
              {t('contacts:detail.selectPrompt')}
            </div>
          )}
        </div>
      </div>

      {/* key: remonta o drawer ao trocar de aba (clientes→fornecedores…) E ao
          alternar criar↔editar ou entre contatos. Sem isto, useForm mantém os
          defaultValues da 1ª montagem (o kind errado, ou os dados do contato
          anterior). `edit-${id}` vs `new-${kind}` garante form fresco. */}
      <StakeholderFormDrawer
        key={emEdicao ? `edit-${emEdicao.id}` : `new-${kind}`}
        kind={kind}
        stakeholder={emEdicao ?? undefined}
        open={drawerAberto}
        onOpenChange={(aberto) => {
          setDrawerAberto(aberto)
          // Ao fechar, volta ao modo criar — a próxima abertura de "Novo" começa limpa.
          if (!aberto) setEmEdicao(null)
        }}
      />
    </>
  )
}
